import cx from "classnames";

import "./Button.scss";
import { Icon, type IconName } from "../Icon";
import {
  ComponentType,
  forwardRef,
  isValidElement,
  ReactElement,
  RefObject,
  useCallback,
} from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../Tooltip/Tooltip";
import type { Placement } from "@floating-ui/react";

type ButtonProps = {
  label?: string | React.ReactNode;
  variant?: "solid" | "ghost" | "destructive" | "outline" | "brand" | "inverse" | "outline-transparent";
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseOver?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseOut?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  type?: "button" | "submit";
  size?: "lg" | "md" | "sm" | "xs";
  iconName?: IconName | ReactElement | ComponentType<any>;
  iconPosition?: "left" | "right";
  iconProps?: Record<string, any>;
  style?: React.CSSProperties;
  className?: string;
  disabled?: boolean;
  href?: string | null;
  target?: string;
  tooltip?: string | null;
  tooltipPlacement?: Placement;
  form?: string;
  children?: React.ReactNode;
};

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      label,
      variant = "solid",
      onClick,
      onMouseOver,
      onMouseOut,
      onMouseDown,
      type = "button",
      size = "md",
      iconName,
      iconPosition = "left",
      iconProps = {},
      style,
      className,
      disabled = false,
      href = null,
      tooltip = null,
      tooltipPlacement = "top",
      children,
      ...restProps
    },
    ref,
  ) => {
    const appliedClasses = cx(
      "Button",
      {
        // Size
        "Button--Large": size === "lg" && label,
        "Button--Medium": size === "md" && label,
        "Button--Small": size === "sm" && label,
        "Button--XSmall": size === "xs" && label,
        "Button--IconLarge": size === "lg" && !label,
        "Button--IconMedium": size === "md" && !label,
        "Button--IconSmall": size === "sm" && !label,
        "Button--IconXSmall": size === "xs" && !label,

        // Variant
        "Button--Solid": variant === "solid",
        "Button--Ghost": variant === "ghost",
        "Button--Destructive": variant === "destructive",
        "Button--Outline": variant === "outline",
        "Button--Brand": variant === "brand",
        "Button--Inverse": variant === "inverse" && label,
        "Button--InverseIcon": variant === "inverse" && !label,
        "Button--OutlineTransparent": variant === "outline-transparent",
      },
      className,
    );

    const renderIcon = useCallback(() => {
      if (!iconName) return null;

      if (typeof iconName === "string") {
        return <Icon iconName={iconName as IconName} {...iconProps} />;
      }

      if (isValidElement(iconName)) {
        return iconName;
      }

      const IconComponent = iconName as ComponentType<any>;
      return <IconComponent {...iconProps} />;
    }, [iconName, iconProps]);

    if (href && !disabled) {
      return (
        <Tooltip placement={tooltipPlacement}>
          <TooltipTrigger>
            <a
              ref={ref as RefObject<HTMLAnchorElement>}
              className={appliedClasses}
              onClick={onClick}
              href={href}
              style={style}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
              onMouseDown={onMouseDown}
              {...restProps}
            >
              {iconName && iconPosition === "left" && (
                <div className={"Button__Icon"}>{renderIcon()}</div>
              )}
              {label && <div className="Button__Label">{label}</div>}
              {iconName && iconPosition === "right" && (
                <div className={"Button__Icon"}>{renderIcon()}</div>
              )}
              {children}
            </a>
          </TooltipTrigger>
          {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
        </Tooltip>
      );
    } else {
      return (
        <Tooltip placement={tooltipPlacement}>
          <TooltipTrigger>
            <button
              ref={ref as RefObject<HTMLButtonElement>}
              className={appliedClasses}
              onClick={onClick}
              type={type}
              style={style}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
              onMouseDown={onMouseDown}
              disabled={disabled}
              {...restProps}
            >
              {iconName && iconPosition === "left" && (
                <div className={"Button__Icon"}>{renderIcon()}</div>
              )}
              {label && <div className="Button__Label">{label}</div>}
              {iconName && iconPosition === "right" && (
                <div className={"Button__Icon"}>{renderIcon()}</div>
              )}
              {children}
            </button>
          </TooltipTrigger>
          {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
        </Tooltip>
      );
    }
  },
);

export { Button };
